import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { xano, type TxListResponse, type TxRow, type TxOrder } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import { PageHeader, StatCard, LoadingState, ErrorState, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { gbp, num, dateUk } from "@/lib/format";
import { Building2, AlertTriangle, ChevronDown, ChevronRight, FlaskConical, Zap } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "review", label: "Needs review" },
  { key: "preview", label: "Preview" },
  { key: "approved", label: "Approved" },
  { key: "posted", label: "Posted" },
];

const treatmentVariant = (key: string): "default" | "secondary" | "warning" => {
  if (key === "domestic") return "default";
  if (key === "eu_ioss") return "warning";
  return "secondary";
};

const statusVariant = (s: string): "secondary" | "warning" | "success" => {
  if (s === "approved") return "warning";
  if (s === "posted") return "success";
  return "secondary";
};

export default function Orders() {
  const { activeToken, tokenLoading } = useCompany();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<number | null>(null);

  const q = useQuery({
    queryKey: ["transactions", activeToken],
    queryFn: () => xano.transactions.list(activeToken!),
    enabled: !!activeToken,
  });

  const refreshMut = useMutation({
    mutationFn: () => xano.transactions.refresh(activeToken!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transactions", activeToken] }),
  });

  const statusMut = useMutation({
    mutationFn: ({ ids, action }: { ids: number[]; action: "approve" | "post" | "reset" }) =>
      xano.transactions.setStatus(activeToken!, ids, action),
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["transactions", activeToken] });
    },
  });

  const data = q.data as TxListResponse | undefined;

  const merged = useMemo(() => {
    if (!data) return [];
    const orderById = new Map<number, TxOrder>(data.orders.map((o) => [o.id, o]));
    return data.rows.map((r) => ({ ...r, order: orderById.get(r.order_id) }));
  }, [data]);

  const counts = useMemo(() => {
    const c = { preview: 0, approved: 0, posted: 0, miscoded: 0 };
    for (const r of merged) {
      if (r.status === "preview") c.preview++;
      else if (r.status === "approved") c.approved++;
      else if (r.status === "posted") c.posted++;
      if (r.miscoded) c.miscoded++;
    }
    return c;
  }, [merged]);

  const filtered = useMemo(() => {
    if (filter === "all") return merged;
    if (filter === "review") return merged.filter((r) => r.miscoded);
    return merged.filter((r) => r.status === filter);
  }, [merged, filter]);

  if (!activeToken && !tokenLoading) {
    return (
      <>
        <PageHeader title="Orders" />
        <EmptyState icon={Building2} title="No company yet" description="Connect a store to build your transactions ledger." />
      </>
    );
  }
  if (tokenLoading || q.isLoading) {
    return (
      <>
        <PageHeader title="Orders" />
        <LoadingState label="Loading transactions…" />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        <PageHeader title="Orders" />
        <ErrorState error={q.error} />
      </>
    );
  }

  const postingSetting = data?.settings?.transaction_posting ?? "preview";
  const postingOff = postingSetting === "off";
  const postLabel = postingSetting === "live" ? "Post live" : "Post to sandbox";

  if (data?.needs_stage) {
    return (
      <>
        <PageHeader
          title="Orders"
          description="The transactions workspace: classify, review, approve and post every order."
        />
        <EmptyState
          icon={FlaskConical}
          title="Stage your orders"
          description="Classify your last 12 months of orders into VAT treatments and draft journals. Nothing posts to your books until you approve it."
          action={
            <Button onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
              {refreshMut.isPending ? "Staging…" : "Stage orders"}
            </Button>
          }
        />
      </>
    );
  }

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.order_id));
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) filtered.forEach((r) => n.delete(r.order_id));
      else filtered.forEach((r) => n.add(r.order_id));
      return n;
    });

  const ids = [...selected];

  return (
    <>
      <PageHeader
        title="Orders"
        description="Classify, review, approve and post every order. Sandbox until you switch posting to live."
        actions={
          <Button variant="secondary" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
            {refreshMut.isPending ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Orders" value={num(data?.summary.total)} />
        <StatCard label="Preview" value={num(counts.preview)} />
        <StatCard label="Approved" value={num(counts.approved)} tone="warning" />
        <StatCard label="Posted" value={num(counts.posted)} tone="success" />
        <StatCard label="Need review" value={num(counts.miscoded)} tone={counts.miscoded ? "destructive" : "success"} />
      </div>

      {/* Posting mode banner */}
      <Card className={postingSetting === "live" ? "mb-4 border-emerald-300 bg-emerald-50" : "mb-4 border-amber-200 bg-amber-50"}>
        <CardContent className="flex items-center gap-2 p-3 text-sm">
          {postingSetting === "live" ? (
            <Zap className="h-4 w-4 text-emerald-600" />
          ) : (
            <FlaskConical className="h-4 w-4 text-amber-600" />
          )}
          <span>
            {postingOff
              ? "Posting is turned off. Turn it on in Settings to post to your books."
              : postingSetting === "live"
              ? "Live posting is on. Approved transactions post to your connected accounting provider."
              : "Sandbox mode. Posting records journals here only, nothing reaches your books. Switch to live in Settings when ready."}
          </span>
        </CardContent>
      </Card>

      {/* Filters + bulk actions */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                (filter === f.key ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && <span className="text-sm text-muted-foreground">{selected.size} selected</span>}
          <Button
            variant="secondary"
            disabled={selected.size === 0 || statusMut.isPending}
            onClick={() => statusMut.mutate({ ids, action: "approve" })}
          >
            Approve
          </Button>
          <Button
            disabled={selected.size === 0 || postingOff || statusMut.isPending}
            onClick={() => statusMut.mutate({ ids, action: "post" })}
            title={postingOff ? "Posting is off in Settings" : undefined}
          >
            {postLabel}
          </Button>
        </div>
      </div>

      {statusMut.isError && (
        <p className="mb-3 text-sm text-destructive">
          {statusMut.error instanceof Error ? statusMut.error.message : "Action failed."}
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2.5">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                  </th>
                  <th className="px-3 py-2.5 text-left font-medium">Order</th>
                  <th className="px-3 py-2.5 text-left font-medium">Date</th>
                  <th className="px-3 py-2.5 text-left font-medium">Destination</th>
                  <th className="px-3 py-2.5 text-right font-medium">Total</th>
                  <th className="px-3 py-2.5 text-left font-medium">Treatment</th>
                  <th className="px-3 py-2.5 text-right font-medium">VAT due</th>
                  <th className="px-3 py-2.5 text-left font-medium">Status</th>
                  <th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <Row
                    key={r.id}
                    r={r}
                    selected={selected.has(r.order_id)}
                    onToggle={() => toggle(r.order_id)}
                    expanded={expanded === r.order_id}
                    onExpand={() => setExpanded(expanded === r.order_id ? null : r.order_id)}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">
                      No orders in this view.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function Row({
  r,
  selected,
  onToggle,
  expanded,
  onExpand,
}: {
  r: TxRow & { order?: TxOrder };
  selected: boolean;
  onToggle: () => void;
  expanded: boolean;
  onExpand: () => void;
}) {
  const o = r.order;
  return (
    <>
      <tr className="border-b last:border-0 hover:bg-muted/20">
        <td className="px-3 py-2.5">
          <input type="checkbox" checked={selected} onChange={onToggle} aria-label="Select order" />
        </td>
        <td className="px-3 py-2.5 font-medium">{o?.order_number || `#${r.order_id}`}</td>
        <td className="px-3 py-2.5 text-muted-foreground">{o ? dateUk(new Date(o.order_created_at).toISOString()) : "—"}</td>
        <td className="px-3 py-2.5">{o?.shipping_country || "—"}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{gbp(o?.total_price, { decimals: true })}</td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <Badge variant={treatmentVariant(r.treatment_key)}>{r.treatment_label}</Badge>
            {r.miscoded && <AlertTriangle className="h-4 w-4 text-destructive" aria-label="Needs review" />}
          </div>
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums">{r.expected_vat > 0 ? gbp(r.expected_vat, { decimals: true }) : "—"}</td>
        <td className="px-3 py-2.5">
          <Badge variant={statusVariant(r.status)}>
            {r.status}
            {r.status === "posted" && r.mode === "sandbox" ? " (sandbox)" : ""}
          </Badge>
        </td>
        <td className="px-3 py-2.5">
          <button onClick={onExpand} className="text-muted-foreground hover:text-foreground" aria-label="Toggle journal">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/20">
          <td colSpan={9} className="px-6 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Journal · {r.tax_code}
            </div>
            <table className="mt-2 w-full max-w-md text-sm">
              <tbody>
                {r.journal.map((l, i) => (
                  <tr key={i}>
                    <td className="py-1">{l.account}</td>
                    <td className="py-1 text-right tabular-nums">{l.debit ? gbp(l.debit, { decimals: true }) : ""}</td>
                    <td className="py-1 text-right tabular-nums">{l.credit ? gbp(l.credit, { decimals: true }) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {r.expected_vat > 0 && r.treatment_key !== "domestic" && (
              <div className="mt-2 text-xs text-muted-foreground">
                Destination VAT to account for under IOSS: {gbp(r.expected_vat, { decimals: true })}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
